var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Contentstack from 'contentstack';
import { ERROR_MESSAGES, getLocale, PLATFORM_STACK_NAME } from '../constants';
import { ContentstackCacheProvider } from './contentstackCacheProvider';
import { mergeDeep, removeEmptyPropsFromObj } from './helpers';
import 'dotenv/config';
const keyMap = {
    apiKey: 'STACK_API_KEY',
    deliveryToken: 'STACK_DELIVERY_TOKEN',
    environment: 'STACK_ENVIRONMENT',
    branch: 'STACK_BRANCH',
};
export function getEnvVariable(stackName, keyName) {
    const stackUpperCase = stackName === null || stackName === void 0 ? void 0 : stackName.toUpperCase();
    return process.env[`${stackUpperCase}_${keyMap[keyName]}`] || '';
}
/**
 * Validates that all necessary secrets are provided to create the instance to ContentStack.
 * ContentStack Authentication: ContentStack SDK function to authenticate and connect the desired stack.
 * The region will always be EU.
 * @param stack - string - CS Stack name (platform | business).
 * @returns new Stack instance
 */
export function initStackInstance(stack) {
    const apiKey = getEnvVariable(stack, 'apiKey');
    const deliveryToken = getEnvVariable(stack, 'deliveryToken');
    const environment = getEnvVariable(stack, 'environment');
    const branch = getEnvVariable(stack, 'branch') || 'main';
    if (!apiKey) {
        throw new Error(`apiKey value for ${stack} stack is undefined`);
    }
    if (!deliveryToken) {
        throw new Error(`deliveryToken value for ${stack} stack is undefined`);
    }
    if (!environment) {
        throw new Error(`environment value for ${stack} stack is undefined`);
    }
    const Stack = Contentstack.Stack({
        api_key: apiKey,
        delivery_token: deliveryToken,
        environment,
        region: Contentstack.Region.EU,
        branch: branch,
    });
    Stack.setCachePolicy(Contentstack.CachePolicy.CACHE_THEN_NETWORK);
    Stack.setCacheProvider(ContentstackCacheProvider());
    return Stack;
}
/**
 * Parses the error and returns a new error instance customized for Contentstack errors
 *
 * @param {Error} error
 * @param stackApiKey
 * @param locale
 * @param contentType
 * @param entryUid
 */
export function contentstackError(error, stackApiKey, locale, contentType = null, entryUid = null) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { error_code, error_message, errors } = error;
    if (error_code && error_message) {
        const message = JSON.stringify({
            message: `${error_code} ${error_message}`,
            errors,
            stackApiKey,
            contentType,
            entryUid,
            url: `https://eu-app.contentstack.com/#!/stack/${stackApiKey}/content-type/${contentType}/${locale}/entry/${entryUid}/edit`,
        }, null, 2);
        return new Error(message);
    }
    else {
        return error;
    }
}
/**
 * If no reference uid (@param entry) was provided, will return all available entries under that contentType.
 * If a reference uid (@param entry) was provided, will return an object containing the entry.
 * @param stack
 * @param locale
 * @param contentType
 * @param entry
 * @param refs
 * @param filter
 */
export function getCMSEntriesForContentType(stack, locale, contentType, entry, refs, filters) {
    return __awaiter(this, void 0, void 0, function* () {
        const { cmsFormat } = typeof locale === 'string' ? getLocale(locale) : locale;
        const Stack = initStackInstance(stack);
        Stack.setCachePolicy(Contentstack.CachePolicy.CACHE_ELSE_NETWORK);
        try {
            if (entry) {
                let entryResponse = Stack.ContentType(contentType).Entry(entry).language(cmsFormat);
                if (refs) {
                    entryResponse = entryResponse.includeReference(refs);
                }
                return yield entryResponse.toJSON().fetch();
            }
            let queryResponse = Stack.ContentType(contentType).Query();
            if (filters) {
                const allFilters = Array.isArray(filters) ? filters : [filters];
                for (const filter of allFilters) {
                    queryResponse[filter.query](filter.key, filter.value);
                }
            }
            if (refs) {
                queryResponse = queryResponse.includeReference(refs);
            }
            const [records, totalCount] = yield queryResponse
                .language(cmsFormat)
                .includeCount()
                .toJSON()
                .find();
            let result = records;
            if (typeof totalCount === 'number' && totalCount > 100) {
                const numOfTotalPages = Math.floor(totalCount / 100);
                for (let pageNum = 1; pageNum <= numOfTotalPages; pageNum++) {
                    let otherQueryResponse = Stack.ContentType(contentType).Query();
                    if (filters) {
                        const allFilters = Array.isArray(filters) ? filters : [filters];
                        for (const filter of allFilters) {
                            otherQueryResponse[filter.query](filter.key, filter.value);
                        }
                    }
                    if (refs) {
                        otherQueryResponse = otherQueryResponse.includeReference(refs);
                    }
                    const [otherRecords] = yield otherQueryResponse
                        .language(cmsFormat)
                        .skip(pageNum * 100)
                        .toJSON()
                        .find();
                    result = [...result, ...otherRecords];
                }
            }
            return result;
        }
        catch (e) {
            console.error(`CMS Error: ${contentType}(${cmsFormat}) - ${(e === null || e === void 0 ? void 0 : e.error_message) || 'Unknown error'}`);
            return {
                locale,
                reference: [],
                isEmpty: true,
            };
        }
    });
}
export function getCMSIcons(stack, locale, entry) {
    return __awaiter(this, void 0, void 0, function* () {
        const contentType = 'icons_set';
        const response = yield getCMSEntriesForContentType(stack, locale, contentType, entry);
        if (!response || !response.file) {
            return { [entry]: undefined };
        }
        return { [entry]: response.file.url };
    });
}
export function fetchDataErrorHandler(error, contentType) {
    // eslint-disable-next-line no-console
    console.error(ERROR_MESSAGES.CS_CONTENT_TYPE_NOT_FOUND({ contentType }), error);
    return {};
}
export function getSettings(locale, settingType, entryUid) {
    return __awaiter(this, void 0, void 0, function* () {
        const settings = yield getCMSEntriesForContentType(PLATFORM_STACK_NAME, locale, `${settingType}_settings`, entryUid).catch((e) => {
            fetchDataErrorHandler(e, `${settingType} settings data`);
        });
        return settings;
    });
}
export function siteSettings(locale, customSettings) {
    return __awaiter(this, void 0, void 0, function* () {
        const globalSettings = yield getSettings(locale, 'global');
        const customSettingsId = customSettings === null || customSettings === void 0 ? void 0 : customSettings.customSettingsId;
        const entryUid = customSettings === null || customSettings === void 0 ? void 0 : customSettings.entryUid;
        if (!customSettingsId && !entryUid) {
            return globalSettings;
        }
        let pageSettings = [];
        if (customSettingsId) {
            const customSettings = yield getSettings(locale, 'custom');
            pageSettings = customSettings.filter((setting) => setting.custom_settings_id === customSettingsId);
            [pageSettings] = pageSettings;
        }
        else if (entryUid) {
            pageSettings = yield getSettings(locale, 'custom', entryUid);
        }
        if (!Object.keys(pageSettings).length) {
            return globalSettings;
        }
        const filteredPageSettings = removeEmptyPropsFromObj(pageSettings);
        const merged = mergeDeep(globalSettings[0], filteredPageSettings);
        return [merged];
    });
}
export function getResizedImageUrl(options) {
    var _a;
    if (options === null || options === void 0 ? void 0 : options.resizableUrl) {
        const urlObj = new URL(options.resizableUrl);
        Object.entries((_a = options.params) !== null && _a !== void 0 ? _a : {}).forEach(([key, value]) => {
            urlObj.searchParams.append(key, value.toString());
        });
        return urlObj.toString();
    }
    return options.fallbackUrl;
}
//# sourceMappingURL=contentstack.js.map